/**
 * Hook tilauslistan hakuun - Infinite scroll + server-side haku
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { hasActiveFilters, applyOrderFilters } from "@/lib/order-helpers";
import { queryKeys } from "@/lib/query-keys";
import type { OrderFilters, OrderTabType, OrderListItem } from "@/types/orders";

const PAGE_SIZE = 50;

export function useOrdersQuery(filters: OrderFilters, activeTab: OrderTabType) {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      ...queryKeys.orders.all,
      filters.status,
      filters.orderType,
      filters.approval,
      filters.dateFrom,
      filters.dateTo,
      filters.search, // Haku queryKey:ssä triggeröi uuden haun
      activeTab,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const hasSearch = !!filters.search?.trim();
      
      // Jos hakutermi on annettu, hae ensin ID:t hakunäkymästä
      let orderIds: string[] | null = null;
      let searchTotalCount: number | null = null;
      
      if (hasSearch) {
        const searchTerm = `%${filters.search}%`;
        
        // Hae order_search_view:sta vain osuvat ID:t
        // Käytetään suoraa SQL-muotoista kyselyä, koska view ei ole tyyppitiedoissa
        let searchQuery = supabase
          .from("order_search_view" as "orders") // Type cast koska view ei ole tyyppitiedoissa
          .select("id", { count: "exact" })
          .or(
            `order_number.ilike.${searchTerm},` +
            `customer_search.ilike.${searchTerm},` +
            `cemetery_name.ilike.${searchTerm},` +
            `partner_name.ilike.${searchTerm},` +
            `config_name.ilike.${searchTerm}`
          )
          .order("order_date", { ascending: false });

        // Apply shared filters (tab, status, orderType, approval, dates)
        searchQuery = applyOrderFilters(searchQuery, filters, activeTab);

        // Paginaatio
        searchQuery = searchQuery.range(pageParam, pageParam + PAGE_SIZE - 1);

        const { data: searchData, error: searchError, count } = await searchQuery;
        
        if (searchError) throw searchError;
        
        // Jos ei hakutuloksia, palauta tyhjä
        if (!searchData || searchData.length === 0) {
          return {
            items: [] as OrderListItem[],
            nextOffset: undefined,
            totalCount: 0,
          };
        }
        
        orderIds = (searchData as unknown as { id: string }[]).map(r => r.id);
        searchTotalCount = count;
      }

      // Hae täydet tilaukset orders-taulusta
      let query = supabase
        .from("orders")
        .select(`
          id,
          order_number,
          order_date,
          order_type,
          fulfillment_type,
          status,
          total_amount,
          notes,
          tracking_token,
          deleted_at,
          requires_cemetery_approval,
          cemetery_approval_status,
          customers (
            id,
            first_name,
            last_name,
            company_name,
            phone,
            email
          ),
          cemeteries (
            id,
            name,
            city
          ),
          partners (
            id,
            name
          ),
          order_lines (
            id,
            description,
            quantity,
            unit_price,
            line_total,
            work_details,
            configuration_id,
            configurations (
              id,
              name,
              product_models (
                id,
                name,
                is_custom_template
              ),
              config_components (
                id,
                quantity,
                width,
                height,
                thickness,
                source_model_name,
                materials (id, name),
                model_components (
                  id,
                  position,
                  component_types (id, name)
                )
              )
            )
          ),
          order_proofs!order_proofs_order_id_fkey (
            id,
            status
          ),
          order_materials (
            id,
            quantity,
            width,
            length,
            thickness,
            materials (
              name
            )
          )
        `, { count: hasSearch ? undefined : "exact" })
        .order("order_date", { ascending: false });

      // Jos hakutulos, suodata ID:llä
      if (orderIds) {
        query = query.in("id", orderIds);
      } else {
        // Ei hakua - suorita normaalit suodattimet (shared helper)
        query = applyOrderFilters(query, filters, activeTab);

        // Paginaatio vain kun ei hakua
        query = query.range(pageParam, pageParam + PAGE_SIZE - 1);
      }

      const { data: ordersData, error: ordersError, count } = await query;

      if (ordersError) throw ordersError;

      const items = (ordersData as unknown as OrderListItem[]) || [];
      const totalCount = hasSearch ? (searchTotalCount || 0) : (count || 0);

      return {
        items,
        nextOffset: items.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined,
        totalCount,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    initialPageParam: 0,
  });

  // Flatten pages into single array
  const allOrders = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.items);
  }, [data?.pages]);

  // Check active filters using helper
  const activeFiltersCheck = hasActiveFilters(filters);

  // Total count from first page
  const totalCount = data?.pages[0]?.totalCount || 0;

  return {
    data: allOrders,
    isLoading,
    error,
    hasActiveFilters: activeFiltersCheck,
    totalCount,
    // Pagination
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    loadedCount: allOrders.length,
  };
}
